import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRole, UserRole } from "@/hooks/useRole";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldX } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireRole?: UserRole;
}

export function ProtectedRoute({ children, requireAuth = true, requireRole }: ProtectedRouteProps) {
  const { user, loading: authLoading, signOut } = useAuth();
  const { hasRole, isLoading: roleLoading } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || roleLoading) return;

    if (requireAuth && !user) {
      // Save the current location to redirect back after login
      const currentPath = window.location.pathname + window.location.search;
      navigate("/auth", { state: { from: currentPath }, replace: true });
      return;
    }

    // Check if user is blacklisted
    if (user && hasRole("blacklist")) {
      signOut();
      return;
    }

    if (requireRole && user && !hasRole(requireRole)) {
      navigate("/", { replace: true });
      return;
    }
  }, [user, authLoading, roleLoading, requireAuth, requireRole, hasRole, navigate, signOut]);

  if (authLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (requireAuth && !user) {
    return null;
  }

  // Block blacklisted users
  if (user && hasRole("blacklist")) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <ShieldX className="h-6 w-6" />
              <CardTitle>Access Denied</CardTitle>
            </div>
            <CardDescription>
              Your account has been suspended. Please contact support for assistance.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (requireRole && user && !hasRole(requireRole)) {
    return null;
  }

  return <>{children}</>;
}
